import { useEffect, useState, useRef, useMemo } from 'react';
import {
   Grid,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Button,
   Paper,
   FormHelperText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../Utils/RichEditor';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CustomDatePicker from '../../Components/MUI/CustomDatePicker';
import { useGetEstimateListQuery } from '../../Redux/Service/EstimateApi';
import { useGetopportunitiesQuery } from '../../Redux/Service/OpportunityApi';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useCreateQuotationMutation } from '../../Redux/Service/QuotationApi';
import { toast } from 'react-toastify';
import { useGetCurrenciesQuery } from '../../Redux/Service/CurrencyApi';
import Loading from '../../Components/Loading';
import defaultSignature from '../../Assets/signature.png';
import InfiniteAutocomplete from '../../Components/InfiniteAutocomplete';
import { API_URL } from '../../Config';
import UploadLogo from '../../Assets/upload.svg';
import StyledDashboardLayout from '../style';
import ServiceProviderSection from './ServiceProviderSection';
import IconButton from '@mui/material/IconButton';
import QuotationDetailFields, { QuotationDetailsHelp } from './QuotationDetailFields';
import {
   detailsSubTotal,
   emptyDetail,
   toPayloadRow,
   validateDetailCost,
} from '../../Utils/quotationDetails';

// Hours in one unit of each time type (One Time is handled by the util)
const hoursPerUnit = (type) => {
   const unit = String(type || '').toLowerCase();
   if (unit === 'days') return 8;
   if (unit === 'months') return 160;
   if (unit === 'years') return 1920;
   return 40;
};

function QuotationAdd() {
   const [loading, setLoading] = useState(true);
   const navigate = useNavigate();
   const [createQuotation] = useCreateQuotationMutation();
   const [opportunity, setOpportunity] = useState({ name: '' });
   const [symbol, setSymbol] = useState('$');
   const [imagePreview, setImagePreview] = useState(null);
   const [serviceProviders, setServiceProviders] = useState([]);
   const [opportunitySymbol, setOpportunitySymbol] = useState('');
   const [opportunityName, setOpportunityName] = useState('');
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const debounceTimeout = useRef(null);
   const [visibleOptions, setVisibleOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const [fileError, setFileError] = useState(null);
   const signatureRef = useRef();

   const {
      data: estimateData,
      isLoading: isLoadingForEstimate,
      isError: isErrorForEstimate,
      error: errorForEstimate,
   } = useGetEstimateListQuery();
   const {
      data: currencies,
      isLoading: isLoadingForCurrency,
      error: errorForCurrency,
   } = useGetCurrenciesQuery();
   const {
      data: opportunityData,
      isLoading: isLoadingForOpportunity,
      isError: isErrorForOpportunity,
      error: errorForOpportunity,
   } = useGetopportunitiesQuery({ limit: 10, name: search, page });

   useEffect(() => {
      if (!isLoadingForEstimate && !isLoadingForCurrency && !isLoadingForOpportunity) {
         setLoading(false);
      }
   }, [isLoadingForEstimate, isLoadingForCurrency, isLoadingForOpportunity]);

   useEffect(() => {
      const newData = opportunityData?.response?.data ?? [];
      setVisibleOptions((prevOptions) => {
         const existingIds = new Set(prevOptions.map((opt) => opt.id));
         const uniqueNewData = newData.filter((opt) => !existingIds.has(opt.id));
         return [...prevOptions, ...uniqueNewData];
      });
   }, [opportunityData?.response?.data]);

   useEffect(() => {
      // Fetch Service Provider list when the page loads
      const fetchServiceProviders = async () => {
         try {
            const response = await fetch(`${API_URL}service`);
            const data = await response.json();
            if (data?.statusCode === 200 && Array.isArray(data.response)) {
               setServiceProviders(data.response);
            }
         } catch (error) {
            console.error('Failed to fetch Service Provider details:', error);
         }
      };

      fetchServiceProviders();
   }, []);

   // Keep the local list in sync after the "Change" button saves a provider
   const handleProviderSaved = (updated) =>
      setServiceProviders((list) =>
         list.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );

   const handleScroll = (event) => {
      const listboxNode = event.currentTarget;
      const bottom =
         listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + 5;

      if (bottom) {
         setPage((prev) => prev + 1);
      }
   };

   const handleOpportunitySelect = (event, value, reason, setFieldValue) => {
      setSelectedOption(value);

      setFieldValue('opportunity', reason === 'selectOption' ? value?.id : null);
      // if (reason === "clear") {
      //     setFieldValue("clientName", "");
      //     setFieldValue("opportunity", null);
      // }

      // if (value) {
      //     setFieldValue("clientName", value.account || "");
      // }
   };

   const removeSignature = (setFieldValue) => {
      signatureRef.current.value = null;
      setImagePreview(null);
      setFieldValue('signature', null);
   };

   const handleSearch = (event, value) => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearch(value);
      }, 300);
   };

   const initialValues = useMemo(
      () => ({
         estimateId: '',
         title: '',
         userId: '',
         name: '',
         place: 'Kolkata',
         clientName: '',
         clientEmail: '',
         clientPhone: '',
         description: '',
         note: '',
         thankYouMessage:
            "<p><strong>T</strong>he above information is not an invoice for the work; this is only an estimation for charges for fixed-cost work. An additional development fee will be charged based on the additional work done by the Service Provider, as agreed upon by Client. At the time of development or once the development is done for a specific module or set of works, changes in functionality will be considered as a 'Change Request,' and additional charges might be applied based on the project manager's decisions. The platform charges (Direct Bank Transfer/Upwork/PayPal Fee) as well as 3rd party API charges, Application Training, and Server Hosting charges, need to be paid separately.</p>",
         validTimeDay: 7,
         gstPercentage: 0,
         gstType: 'none',
         date: null, // changed from new Date() so validation can trigger
         currencyId: '',
         signature: '',
         opportunity: '',
         serviceProviderId: '',
         serviceProviderName: '',
         serviceProviderEmail: '',
         serviceProviderPhone: '',
         quotationDetails: [emptyDetail(1)],
      }),
      [],
   );

   const emailRegex = /^(?!.*\.\.)(?!.*@@)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

   const validationSchema = Yup.object().shape({
      estimateId: Yup.string().optional(),
      place: Yup.string()
         .required('Place is required')
         .matches(/^[A-Za-z\s]+$/, 'Place can contain only letters and spaces'),
      title: Yup.string()
         .required('Quotation is required'),
      description: Yup.string()
         .required('Description is required'),
      // note: Yup.string()
      //    .required('Note is required')
      thankYouMessage: Yup.string()
         .required('Thank You Message is required'),
      validTimeDay: Yup.number()
         .required('Valid time in days is required')
         .min(1, 'Valid For (Days) must be 1 or more')
         .max(30, 'Valid For (Days) should be at most 30 days'),
      // gstPercentage: Yup.number().min(0, 'GST Percentage cannot be a negative number'),
      gstType: Yup.string().required('GST Type is required').default('none'),
      date: Yup.date().nullable().typeError('Date is required').required('Date is required'),
      currencyId: Yup.number()
         .required('Please select a Currency')
         .typeError('Please select a Currency'),
      opportunity: Yup.number()
         .required('Opportunity is required')
         .typeError('Opportunity is required'),
      serviceProviderId: Yup.number()
         .required('Service Provider is required')
         .typeError('Service Provider is required'),
      clientName: Yup.string()
         .required('Client Name is required'),
      serviceProviderPhone: Yup.string()
         .required('Service Provider Phone is required'),
      serviceProviderEmail: Yup.string()
         // .email('Invalid email')
         .test('valid-email', 'Please enter a valid email address', (value) => {
            if (!value) return true;
            if (value.startsWith('.')) return false;
            if (value.endsWith('.')) return false;
            if (value.includes('..')) return false;
            return emailRegex.test(value);
         })
         .required('Service Provider Email is required'),
      clientEmail: Yup.string()
         .test('valid-email', 'Please enter a valid email address', (value) => {
            if (!value) return true;
            if (value.startsWith('.')) return false;
            if (value.endsWith('.')) return false;
            if (value.includes('..')) return false;
            return emailRegex.test(value);
         })
         .test(
            'email-or-phone',
            'Client email or phone number is required',
            (value, ctx) => !!(value?.trim() || ctx.parent.clientPhone?.trim()),
         ),
      clientPhone: Yup.string(),
      quotationDetails: Yup.array().of(
         Yup.object()
            .shape({
               description: Yup.string()
                  .required('Description is required')
                  .max(250, 'Description should be at most 250 characters'),
               type: Yup.string(),
               developmentTime: Yup.number()
                  .nullable()
                  .positive('Development Time must be a positive number')
                  .max(9999, 'Development Time should be at most 9999 hours')
                  .integer(),
               chargePerHour: Yup.number()
                  .nullable()
                  .positive('Charge Per Hour must be a positive number')
                  .max(99999, 'Charge Per Hour should be at most 99999'),
               total: Yup.number().nullable().min(0, 'Cost cannot be negative'),
            })
            .test('detail-cost', function (row) {
               const problem = validateDetailCost(row);
               return problem
                  ? this.createError({
                       path: `${this.path}.${problem.field}`,
                       message: problem.message,
                    })
                  : true;
            }),
      ),
   });

   const handleSubTotal = (values) => {
      return detailsSubTotal(values?.quotationDetails, hoursPerUnit);
   };

   const gstTotal = (values) => {
      const gst = values.gstType !== 'none' ? 18 : 0;
      const gstPercentage = Number(gst / 100);
      const gstTotal = Number(handleSubTotal(values) * gstPercentage);
      return gstTotal.toFixed(2);
   };

   const allTotal = (values) => {
      const alltotal = Number(Number(gstTotal(values)) + Number(handleSubTotal(values)));
      return alltotal.toFixed(2);
   };

   const handleEstimatesOnChange = (e, setFieldValue) => {
      const estimate = estimateData?.response?.find((val) => e.target.value === val.id);
      if (estimate) {
         setFieldValue('estimateId', e.target.value);
      }
   };

   const onImageChange = (event, setFieldValue) => {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

      if (!allowedTypes.includes(file.type)) {
         toast.error('Only JPG, PNG files are allowed');
         setFileError('Invalid file type. Only JPG, PNG files are allowed');
         event.target.value = '';
         setFieldValue('signature', null);
         setImagePreview(null);
         return;
      }
      setFileError(null);
      setFieldValue('signature', file);
      setImagePreview(URL.createObjectURL(file));
   };

   const isInr = (values) =>
      currencies?.response?.find((val) => Number(val.id) === Number(values.currencyId))?.code === 'INR';

   const currencySymbolSet = (currencyId, setFieldValue) => {
      setFieldValue('currencyId', currencyId);
      const selectedCurrency = currencies?.response?.find((val) => val.id === currencyId);
      if (selectedCurrency?.code !== 'INR') setFieldValue('gstType', 'none');
      setSymbol(selectedCurrency?.symbol ?? '$');
   };

   const handleSubmit = async (values) => {
      try {
         values.quotationDetails = values.quotationDetails.map((row) =>
            toPayloadRow(row, hoursPerUnit),
         );
         values.userId = 1;
         // Include pre-calculated totals from frontend
         // values.subTotal = handleSubTotal(values);
         // values.gstTotal = Number(gstTotal(values));
         // values.total = Number(allTotal(values));
         if (!values.estimateId) {
            delete values.estimateId;
         }
         if (
            values.currencyId !== '' &&
            values.currencyId !== null &&
            values.currencyId !== undefined
         ) {
            values.currencyId = parseInt(values.currencyId);
         }
         const createQuotationData = await createQuotation(values);
         if (createQuotationData && createQuotationData?.error) {
            toast.error(createQuotationData?.error?.data?.message);
         } else if (createQuotationData) {
            toast.success(createQuotationData.data?.message);
            navigate('/quotation');
         }
      } catch (error) {
         toast.error('Something went wrong, Please try again later!');
         console.error('Submission failed', error);
      }
   };

   const handleRemoveItem = (id, arrayHelpers) => {
      arrayHelpers.remove(id);
   };

   if (loading) {
      return <Loading />;
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Add Quotation</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/quotation" className="btn addBtn">
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <div className="card customCard p-3 m-3">
               {/* <div className="card-header">
                        <h3 className="card-title">Quotation</h3>
                    </div> */}
               {isLoadingForCurrency || isLoadingForEstimate || isLoadingForOpportunity ? (
                  <div>Loading...</div>
               ) : errorForCurrency || errorForEstimate || errorForOpportunity ? (
                  <div>
                     Error: {errorForCurrency?.message}
                     {errorForEstimate?.message}
                     {errorForOpportunity?.message}
                  </div>
               ) : (
                  <Formik
                     initialValues={initialValues}
                     enableReinitialize={false}
                     validationSchema={validationSchema}
                     onSubmit={(values) => handleSubmit(values)}
                  >
                     {({
                        isSubmitting,
                        values,
                        setFieldValue,
                        setFieldTouched,
                        touched,
                        errors,
                        handleBlur,
                     }) => (
                        <Form>
                           <Box className="card-body" sx={{ padding: '15px 0' }}>
                              <Grid container spacing={2}>
                                 {/* ── Quotation Section ── */}
                                 <Grid item md={12} xs={12}>
                                    <Box className="commonBox">
                                       <Typography variant="h6" gutterBottom>
                                          Quotation
                                       </Typography>
                                       <Grid container spacing={4}>
                                          <Grid item md={6} xs={12}>
                                             <label htmlFor="date">Date*</label>
                                             <CustomDatePicker
                                                label="Date"
                                                name="date"
                                                onChange={(date) => setFieldValue('date', date)}
                                                error={touched.date && Boolean(errors.date)}
                                                helperText={touched.date && errors.date}
                                             />
                                             {/* <ErrorMessage
                                                name="date"
                                                component="div"
                                                style={{ color: 'red' }}
                                             /> */}
                                             {touched.date && errors.date && (
                                                <FormHelperText
                                                   sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                >
                                                   {touched.date && errors.date}
                                                </FormHelperText>
                                             )}
                                          </Grid>

                                          <Grid item md={6} xs={12}>
                                             <label htmlFor="validTimeDay">Valid For (Days)*</label>
                                             <Field
                                                fullWidth
                                                label="Valid For (Days)"
                                                name="validTimeDay"
                                                variant="outlined"
                                                type="number"
                                                as={TextField}
                                                error={
                                                   touched.validTimeDay &&
                                                   Boolean(errors.validTimeDay)
                                                }
                                                helperText={
                                                   touched.validTimeDay && errors.validTimeDay
                                                }
                                             />
                                          </Grid>

                                          <Grid item md={6} xs={12}>
                                             <FormControl fullWidth>
                                                <label htmlFor="opportunity">Opportunity*</label>
                                                <InfiniteAutocomplete
                                                   onBlur={handleBlur}
                                                   value={selectedOption}
                                                   onChange={(event, value, reason) =>
                                                      handleOpportunitySelect(
                                                         event,
                                                         value,
                                                         reason,
                                                         setFieldValue,
                                                      )
                                                   }
                                                   options={visibleOptions}
                                                   onScroll={handleScroll}
                                                   inputValue={search}
                                                   setInputValue={handleSearch}
                                                   total_options={'Opportunity'}
                                                   error={Boolean(
                                                      touched.opportunity && errors.opportunity,
                                                   )}
                                                   helperText={
                                                      touched.opportunity && errors.opportunity
                                                   }
                                                   name="opportunity"
                                                />
                                             </FormControl>
                                          </Grid>

                                          <Grid item md={6} xs={12}>
                                             <label htmlFor="title">Quotation Title*</label>
                                             <Field
                                                fullWidth
                                                label="Quotation Title"
                                                name="title"
                                                variant="outlined"
                                                type="text"
                                                as={TextField}
                                                error={touched.title && Boolean(errors.title)}
                                                helperText={touched.title && errors.title}
                                             />
                                          </Grid>

                                          <Grid item md={6} xs={12}>
                                             {isLoadingForEstimate ? (
                                                <div>Loading...</div>
                                             ) : isErrorForEstimate ? (
                                                <div>Error: {errorForEstimate?.message}</div>
                                             ) : (
                                                <>
                                                   <label htmlFor="estimateId">
                                                      Estimates (Optional)
                                                   </label>
                                                   <FormControl fullWidth>
                                                      {/* <label htmlFor="estimateId">Estimates</label> */}
                                                      <InputLabel
                                                         id="demo-select-small-label"
                                                         sx={
                                                            touched.estimateId &&
                                                            Boolean(errors.estimateId)
                                                               ? { color: 'hwb(353deg 2% 13%)' }
                                                               : {}
                                                         }
                                                      >
                                                         Estimates
                                                      </InputLabel>
                                                      <Field
                                                         label="Estimates"
                                                         name="estimateId"
                                                         as={Select}
                                                         labelId="demo-select-small-label"
                                                         // id="demo-simple-select"
                                                         onChange={(event) =>
                                                            handleEstimatesOnChange(
                                                               event,
                                                               setFieldValue,
                                                            )
                                                         }
                                                         error={
                                                            touched.estimateId &&
                                                            Boolean(errors.estimateId)
                                                         }
                                                         helperText={
                                                            touched.estimateId && errors.estimateId
                                                         }
                                                      >
                                                         {estimateData?.response?.map(
                                                            (val, index) => (
                                                               <MenuItem key={index} value={val.id}>
                                                                  {val.name}
                                                               </MenuItem>
                                                            ),
                                                         )}
                                                      </Field>
                                                   </FormControl>
                                                </>
                                             )}
                                          </Grid>

                                          <Grid item md={6} xs={12}>
                                             <label htmlFor="currencyId">Currency*</label>
                                             <FormControl
                                                fullWidth
                                                error={
                                                   touched.currencyId && Boolean(errors.currencyId)
                                                }
                                             >
                                                <InputLabel id="currency-label">
                                                   Currency
                                                </InputLabel>
                                                <Field
                                                   label="Currency"
                                                   name="currencyId"
                                                   as={Select}
                                                   labelId="currency-label"
                                                   onChange={(event) =>
                                                      currencySymbolSet(
                                                         event.target.value,
                                                         setFieldValue,
                                                      )
                                                   }
                                                   error={Boolean(
                                                      touched.currencyId && errors.currencyId,
                                                   )}
                                                >
                                                   {currencies?.response?.map((val) => (
                                                      <MenuItem key={val.id} value={val.id}>
                                                         {val.name} ({val.symbol})
                                                      </MenuItem>
                                                   ))}
                                                </Field>
                                                {touched.currencyId && errors.currencyId && (
                                                   <FormHelperText color="error">
                                                      {touched.currencyId && errors.currencyId}
                                                   </FormHelperText>
                                                )}
                                             </FormControl>
                                          </Grid>
                                       </Grid>
                                    </Box>
                                 </Grid>

                                 {/* ── Service Provider Section ── */}
                                 <Grid item xs={12}>
                                    <ServiceProviderSection
                                       serviceProviders={serviceProviders}
                                       onProviderSaved={handleProviderSaved}
                                    />
                                 </Grid>

                                 {/* ── Client Details Section ── */}
                                 <Grid item xs={12}>
                                    <Box className="commonBox">
                                       <Typography variant="h6" gutterBottom>
                                          Client Details*
                                       </Typography>
                                       <Grid container spacing={2}>
                                          <Grid item md={4} xs={12}>
                                             <label htmlFor="clientName">Name*</label>
                                             <Field
                                                fullWidth
                                                label="Client Name"
                                                name="clientName"
                                                variant="outlined"
                                                as={TextField}
                                                error={
                                                   touched.clientName && Boolean(errors.clientName)
                                                }
                                                helperText={touched.clientName && errors.clientName}
                                             />
                                          </Grid>
                                          <Grid item md={4} xs={12}>
                                             <label htmlFor="clientEmail">Email</label>
                                             <Field
                                                fullWidth
                                                label="Client Email"
                                                name="clientEmail"
                                                variant="outlined"
                                                as={TextField}
                                                error={
                                                   touched.clientEmail &&
                                                   Boolean(errors.clientEmail)
                                                }
                                                helperText={
                                                   touched.clientEmail && errors.clientEmail
                                                }
                                             />
                                          </Grid>
                                          <Grid item md={4} xs={12}>
                                             <label htmlFor="clientPhone">Phone Number</label>
                                             <Field
                                                fullWidth
                                                label="Client Phone Number"
                                                name="clientPhone"
                                                variant="outlined"
                                                as={TextField}
                                                error={
                                                   touched.clientPhone &&
                                                   Boolean(errors.clientPhone)
                                                }
                                                helperText={
                                                   touched.clientPhone && errors.clientPhone
                                                }
                                             />
                                          </Grid>
                                       </Grid>
                                    </Box>
                                 </Grid>

                                 {/* ── Project Description Section ── */}
                                 <Grid item xs={12}>
                                    <Box className="commonBox">
                                       <Typography variant="h6" gutterBottom>
                                          Project Description*
                                       </Typography>
                                       <Grid container spacing={4}>
                                          <Grid item xs={12}>
                                             <Field
                                                name="description"
                                                error={
                                                   touched.description &&
                                                   Boolean(errors.description)
                                                }
                                                helperText={
                                                   touched.description && errors.description
                                                }
                                                placeholder="Enter Project Description here..."
                                             >
                                                {({ field }) => (
                                                   <CKEditor
                                                      editor={ClassicEditor}
                                                      data={field.value}
                                                      config={{
                                                         placeholder:
                                                            'Enter Project Description here...',
                                                      }}
                                                      onChange={(event, editor) =>
                                                         setFieldValue(
                                                            'description',
                                                            editor.getData(),
                                                         )
                                                      }
                                                   />
                                                )}
                                             </Field>
                                             {touched.description && errors.description && (
                                                <FormHelperText
                                                   sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                >
                                                   {touched.description && errors.description}
                                                </FormHelperText>
                                             )}
                                             {/* <ErrorMessage
                                                name="description"
                                                component="div"
                                                style={{ color: 'red' }}
                                             /> */}
                                          </Grid>
                                       </Grid>
                                    </Box>
                                 </Grid>

                                 {/* ── Development Time & Cost Section ── */}
                                 <Grid item xs={12}>
                                    <Box className="commonBox">
                                       <Typography variant="h6" gutterBottom>
                                          Development Time & Cost Details*
                                       </Typography>
                                       {/* <QuotationDetailsHelp /> */}
                                       <FieldArray name="quotationDetails">
                                          {(arrayHelpers) => (
                                             <div>
                                                {values.quotationDetails.map((val, index) => (
                                                   <Grid
                                                      container
                                                      spacing={2}
                                                      key={val.id}
                                                      sx={{ my: 1 }}
                                                      alignItems="flex-start"
                                                   >
                                                      <QuotationDetailFields
                                                         index={index}
                                                         symbol={symbol}
                                                         hoursPerUnit={hoursPerUnit}
                                                      />

                                                      {/* Add / Remove row actions */}
                                                      <Grid item xs={12}>
                                                         <Box
                                                            sx={{
                                                               display: 'flex',
                                                               justifyContent: 'space-between',
                                                               alignItems: 'center',
                                                            }}
                                                         >
                                                            {index ===
                                                               values.quotationDetails.length -
                                                                  1 && (
                                                               <IconButton
                                                                  size="small"
                                                                  sx={{
                                                                     color: '#333',
                                                                     fontSize: 16,
                                                                  }}
                                                                  onClick={() =>
                                                                     arrayHelpers.push(
                                                                        emptyDetail(
                                                                           values.quotationDetails
                                                                              .length + 1,
                                                                        ),
                                                                     )
                                                                  }
                                                               >
                                                                  <AddIcon fontSize="small" />
                                                                  Add
                                                               </IconButton>
                                                            )}

                                                            {index !== 0 && (
                                                               <IconButton
                                                                  size="small"
                                                                  sx={{ color: '#333' }}
                                                                  onClick={() =>
                                                                     handleRemoveItem(
                                                                        index,
                                                                        arrayHelpers,
                                                                     )
                                                                  }
                                                               >
                                                                  <DeleteIcon fontSize="small" />
                                                               </IconButton>
                                                            )}
                                                         </Box>
                                                      </Grid>
                                                   </Grid>
                                                ))}
                                             </div>
                                          )}
                                       </FieldArray>

                                       {/* Sub Total – commented out
                                       <Grid container spacing={2} sx={{ mt: 4 }}>
                                          <Grid item xs={12} sx={{ my: 2 }}>
                                             <Grid container spacing={2}>
                                                <Grid item xs={2} alignItems="center">
                                                   <h4 style={{ margin: 0 }}>Sub Total :</h4>
                                                </Grid>
                                                <Grid item xs={2}>
                                                   <Box
                                                      sx={{
                                                         border: '1px solid grey',
                                                         borderRadius: '5px',
                                                         p: 2,
                                                      }}
                                                   >
                                                      <b>{symbol}</b> {handleSubTotal(values)}
                                                   </Box>
                                                </Grid>
                                             </Grid>
                                          </Grid>
                                       </Grid> */}

                                       {/* Signature + GST row */}
                                       <Grid
                                          container
                                          spacing={2}
                                          sx={{ mt: 2 }}
                                          alignItems="center"
                                          justifyContent="space-between"
                                       >
                                          <Grid item xs={12} md={2}>
                                             <Box sx={{ border: '1px solid grey', p: 1 }}>
                                                {imagePreview ? (
                                                   <img
                                                      src={imagePreview}
                                                      alt="Signature Preview"
                                                      style={{
                                                         width: '100%',
                                                         height: '100%',
                                                         objectFit: 'cover',
                                                      }}
                                                   />
                                                ) : (
                                                   <img
                                                      src={defaultSignature}
                                                      alt="signature"
                                                      style={{
                                                         width: '100%',
                                                         height: '100%',
                                                         objectFit: 'cover',
                                                      }}
                                                   />
                                                )}
                                             </Box>
                                             {values.signature && (
                                                <button
                                                   type="button"
                                                   onClick={() => removeSignature(setFieldValue)}
                                                   style={{
                                                      marginTop: '10px',
                                                      padding: '5px 10px',
                                                      border: 'none',
                                                      fontSize: '13px',
                                                      backgroundColor: 'red',
                                                      color: '#fff',
                                                      borderRadius: '7px',
                                                   }}
                                                >
                                                   Remove Signature
                                                </button>
                                             )}
                                          </Grid>

                                          <Grid item xs={12} md={5} />
                                         {isInr(values) && (
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '15px',
                                                width: { xs: '100%', md: 'auto' },
                                                minWidth: { md: 300 },
                                                px: 2,
                                             }}
                                          >
                                             <h4>GST (%):</h4>
                                             <FormControl
                                                fullWidth
                                                error={touched.gstType && Boolean(errors.gstType)}
                                             >
                                                <InputLabel id="gst-label">GST</InputLabel>
                                                <Select
                                                   labelId="gst-label"
                                                   label="GST"
                                                   name="gstType"
                                                   value={values.gstType}
                                                   onChange={(e) =>
                                                      setFieldValue('gstType', e.target.value)
                                                   }
                                                >
                                                   <MenuItem selected value="none">
                                                      N/A
                                                   </MenuItem>
                                                   <MenuItem value="cgst_sgst">
                                                      CGST(9%) + SGST(9%)
                                                   </MenuItem>
                                                   <MenuItem value="igst">IGST(18%)</MenuItem>
                                                </Select>
                                                <FormHelperText>
                                                   {touched.gstType && errors.gstType
                                                      ? errors.gstType
                                                      : 'Select GST type'}
                                                </FormHelperText>
                                             </FormControl>
                                          </Box>
                                         )}

                                          {/* <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                             
                                          </Grid>

                                          <Grid item xs={12} md={3}>
                                             GST amount box – commented out
                                             <Grid item xs={1}>
                                                <Box
                                                   sx={{
                                                      border: '1px solid grey',
                                                      borderRadius: '5px',
                                                      p: 2,
                                                      px: 2,
                                                      py: 1
                                                   }}
                                                >
                                                   <b>{symbol}</b> {gstTotal(values)}
                                                </Box>
                                             </Grid>
                                          </Grid> */}
                                       </Grid>

                                       {/* Upload signature + Place + Total row */}
                                       <Grid
                                          container
                                          spacing={2}
                                          sx={{ mt: 2 }}
                                          alignItems="flex-start"
                                       >
                                          <Grid item xs={12}>
                                             <Paper elevation={2} className="uploadFile">
                                                <Box>
                                                   <img
                                                      src={UploadLogo}
                                                      alt="Upload Logo"
                                                      className="uploadLogo"
                                                   />
                                                   <Typography variant="body2" sx={{ mt: 1 }}>
                                                      Drag and Drop or{' '}
                                                      <Link
                                                         component="button"
                                                         variant="body2"
                                                         className="chooseFile"
                                                         onClick={() =>
                                                            document
                                                               .getElementById('signature')
                                                               .click()
                                                         }
                                                      >
                                                         Choose Signature
                                                      </Link>{' '}
                                                      to upload
                                                   </Typography>
                                                   <Typography
                                                      sx={{
                                                         fontSize: '.95rem',
                                                         color: 'green',
                                                         fontWeight: 400,
                                                      }}
                                                   >
                                                      *Only jpg, jpeg, and png files
                                                   </Typography>
                                                   {fileError && (
                                                      <Typography
                                                         sx={{
                                                            fontSize: '.95rem',
                                                            color: 'red',
                                                            fontWeight: 400,
                                                         }}
                                                      >
                                                         {fileError}
                                                      </Typography>
                                                   )}
                                                </Box>
                                                <input
                                                   ref={signatureRef}
                                                   id="signature"
                                                   type="file"
                                                   accept="image/*"
                                                   style={{ display: 'none' }}
                                                   onChange={(event) =>
                                                      onImageChange(event, setFieldValue)
                                                   }
                                                />
                                             </Paper>
                                             <ErrorMessage
                                                name="signature"
                                                component="div"
                                                className="error-message"
                                             />
                                          </Grid>
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                gap: '15px',
                                                justifyContent: 'space-between',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                width: '100%',
                                                padding: { xs: '16px 0 0 16px', sm: '0 0 0 20px' },
                                             }}
                                          >
                                             <Field
                                                label="Place"
                                                name="place"
                                                variant="outlined"
                                                type="text"
                                                as={TextField}
                                                error={touched.place && Boolean(errors.place)}
                                                helperText={touched.place && errors.place}
                                             />
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   flexWrap: 'wrap',
                                                   gap: '10px',
                                                }}
                                             >
                                                <Grid item>
                                                   <h4>Total(Including GST Only) :</h4>
                                                </Grid>
                                                <Grid item>
                                                   <Box
                                                      sx={{
                                                         border: '1px solid grey',
                                                         borderRadius: '5px',
                                                         p: 2,
                                                         px: 2,
                                                         py: 1,
                                                      }}
                                                   >
                                                      <b>{symbol}</b> {allTotal(values)}
                                                   </Box>
                                                </Grid>
                                             </Box>
                                          </Box>
                                       </Grid>
                                    </Box>
                                 </Grid>

                                 {/* ── Note Section ── */}
                                 <Grid item xs={12}>
                                    <Box className="commonBox">
                                       <Typography variant="h6" gutterBottom>
                                          Note
                                       </Typography>
                                       <Grid container spacing={4}>
                                          <Grid item xs={12}>
                                             <Field name="note">
                                                {({ field }) => (
                                                   <CKEditor
                                                      editor={ClassicEditor}
                                                      data={field.value}
                                                      config={{
                                                         placeholder: 'Enter Note here...',
                                                      }}
                                                      onChange={(event, editor) =>
                                                         setFieldValue('note', editor.getData())
                                                      }
                                                      // error={touched.note && Boolean(errors.note)}
                                                      // helperText={touched.note && errors.note}
                                                   />
                                                )}
                                             </Field>
                                             {/* <ErrorMessage
                                                name="note"
                                                component="div"
                                                style={{ color: 'red' }}
                                             /> */}
                                             {/* {touched.note && (
                                                <FormHelperText
                                                   sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                >
                                                   {touched.note && errors.note}
                                                </FormHelperText>
                                             )} */}
                                          </Grid>
                                       </Grid>
                                    </Box>
                                 </Grid>

                                 {/* ── Thank You Section ── */}
                                 <Grid item xs={12}>
                                    <Box className="commonBox">
                                       <Typography variant="h6" gutterBottom>
                                          Thank You for Your Business!
                                       </Typography>
                                       <Grid container spacing={4}>
                                          <Grid item xs={12}>
                                             <Field
                                                name="thankYouMessage"
                                                error={
                                                   touched.thankYouMessage && errors.thankYouMessage
                                                }
                                             >
                                                {({ field }) => (
                                                   <CKEditor
                                                      editor={ClassicEditor}
                                                      data={field.value}
                                                      config={{
                                                         placeholder:
                                                            'Enter thank you message here...',
                                                      }}
                                                      onChange={(event, editor) =>
                                                         setFieldValue(
                                                            'thankYouMessage',
                                                            editor.getData(),
                                                         )
                                                      }
                                                   />
                                                )}
                                             </Field>
                                             {/* <ErrorMessage
                                                name="thankYouMessage"
                                                component="div"
                                                style={{ color: 'red' }}
                                             /> */}
                                             {touched.thankYouMessage && (
                                                <FormHelperText
                                                   sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                >
                                                   {touched.thankYouMessage &&
                                                      errors.thankYouMessage}
                                                </FormHelperText>
                                             )}
                                          </Grid>
                                       </Grid>
                                    </Box>
                                 </Grid>
                              </Grid>

                              <button
                                 type="submit"
                                 className="btn commonBtn mt-2"
                                 disabled={isSubmitting}
                              >
                                 {isSubmitting ? 'Creating...' : 'Submit'}
                              </button>
                           </Box>
                        </Form>
                     )}
                  </Formik>
               )}
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default QuotationAdd;
